import { ThemeConfig, theme as antdTheme } from 'antd';
import type { ThemeMode } from '@shared/store/theme.store';

/**
 * Hex values live here (not as `var(--color-*)`) because antd's algorithm derives hover/active/disabled shades in JS
 * from these seeds and cannot do color math on a CSS variable. Component tokens below that are plain values keep using
 * `var(--color-*)`, so they follow the `[data-theme]` palette in `src/styles/App.css` automatically.
 */
const palette = {
  light: {
    colorPrimary: '#003399',
    colorBorder: '#ececef',
    colorText: '#4a4d53',
    menuSelectedBg: '#e0e0e0',
    menuGroupTitle: '#5F6062',
    radioButtonColor: '#5f6062',
    radioCheckedBg: '#E5EAF5',
    tableHeaderBg: '#F9FAFB',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    selectText: '#5F6062',
  },
  dark: {
    colorPrimary: '#3b82f6',
    colorBorder: '#373b42',
    colorText: '#c6c9cf',
    menuSelectedBg: '#2a2d33',
    menuGroupTitle: '#9ba0a9',
    radioButtonColor: '#b0b4bc',
    radioCheckedBg: '#19305a',
    tableHeaderBg: '#1f2226',
    colorBgContainer: '#1a1c20',
    colorBgElevated: '#1f2226',
    selectText: '#b0b4bc',
  },
} satisfies Record<ThemeMode, Record<string, string>>;

export const getTheme = (mode: ThemeMode): ThemeConfig => {
  const colors = palette[mode];

  return {
    algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      fontFamily: 'Outfit', // default application font
      fontSize: 16, // default application font size
      colorPrimary: colors.colorPrimary, // primary antd color
      colorBorder: colors.colorBorder, // all border colors
      colorText: colors.colorText, // text color across app
      colorBgContainer: colors.colorBgContainer, // inputs, buttons, cards (matches --color-surface)
      colorBgElevated: colors.colorBgElevated, // modals, dropdowns, popovers

      colorSuccess: '#047857',
      colorInfo: colors.colorPrimary,
      colorWarning: '#D97706',
      colorError: '#EF4444',
    },
    components: {
      Badge: {
        fontSize: 11,
        lineHeight: 1.2,
        indicatorHeight: 20,
        borderRadius: 20,
        textFontSize: 11,
        textFontWeight: 500,
        padding: 1,
      },
      Input: {
        lineHeight: 2.4,
        colorText: 'var(--color-text-700)',
        colorBorder: 'var(--color-gray-600)',
        fontSize: 14,
        fontSizeLG: 15,
      },
      Button: {
        controlHeight: 40,
        fontSize: 15,
        fontWeight: 'medium',
        primaryShadow: 'none',
        defaultShadow: 'none',
        colorPrimaryHover: 'var(--color-primary-600)',
        borderRadius: 8,
        paddingInline: 16,
      },
      Dropdown: {
        controlItemBgHover: 'var(--color-gray-100)',
      },
      Menu: {
        subMenuItemBg: 'transparent', // used for sidebar numbers
        itemSelectedBg: colors.menuSelectedBg,
        itemSelectedColor: '',
        groupTitleColor: colors.menuGroupTitle,
        groupTitleFontSize: 12,
      },
      Radio: {
        buttonColor: colors.radioButtonColor,
        buttonCheckedBg: colors.radioCheckedBg,
        controlHeight: 38,
      },
      Table: {
        fontSize: 14,
        fontWeightStrong: 500,
        rowHoverBg: 'var(--color-gray-100)',
        cellPaddingBlock: 10,
        cellPaddingInline: 16,
        headerBg: colors.tableHeaderBg,
      },
      Layout: {
        bodyBg: 'var(--page-background)',
      },
      Select: {
        fontSize: 14,
        fontWeightStrong: 500,
        controlHeight: 36,
        colorText: colors.selectText,
        colorTextPlaceholder: 'var(--color-text-400)',
      },
      Steps: {
        titleLineHeight: 20,
      },
      Skeleton: {
        titleHeight: 10,
        paragraphLiHeight: 10,
        gradientFromColor: 'var(--color-primary-50)',
        gradientToColor: 'var(--color-primary-100)',
      },
      Switch: {
        colorPrimary: 'var(--color-emerald-500)',
        colorPrimaryHover: 'var(--color-emerald-400)',
        trackMinWidth: 52,
        trackHeight: 28,
        handleSize: 24,
      },
      Tabs: {
        colorText: 'var(--color-text-800)',
      },
      Upload: {
        colorBorder: 'var(--color-primary-200)',
        borderRadiusLG: 12,
      },
    },
  };
};
